import { HttpBackend, HttpClient } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { map, shareReplay } from "rxjs/operators";
import { LIB_ENVIRONMENT } from "../vnext/services/environment.token";
import { CommonLibraryEnvironment } from "../vnext/services/models";
import { isManualPageName, ManualPage, ManualPageMissingError, parseManualPage, PUBLIC_MANUAL_BASE } from "./manual-html";

/** One entry in the technical manual's contents (Loans API, work item 882). */
export interface TechnicalManualEntry {
  page: string;
  title: string;
}

/** The technical manual's title and pages in reading order. */
export interface TechnicalManualContents {
  title: string;
  pages: TechnicalManualEntry[];
}

interface TechnicalManualPageDto {
  page: string;
  title: string;
  html: string;
}

@Injectable({ providedIn: "root" })
/**
 * Loads pages of the user manual and the technical manual. Public pages come from the static copy
 * in the backoffice build; technical pages from the Loans API, which only serves them to holders of
 * View Technical Manual.
 */
export class ManualService {
  // Public pages are static files any visitor may open, so they are fetched without the app's
  // interceptors: no bearer token goes with them and a failure never triggers the 401 sign-in redirect.
  private readonly staticHttp: HttpClient;
  private readonly publicPages = new Map<string, Observable<ManualPage>>();

  constructor(
    private http: HttpClient,
    backend: HttpBackend,
    @Inject(LIB_ENVIRONMENT) private env: CommonLibraryEnvironment,
  ) {
    this.staticHttp = new HttpClient(backend);
  }

  /** A page of the public user manual, fetched once per session. */
  publicPage(file: string): Observable<ManualPage> {
    if (!isManualPageName(file)) return throwError(() => new ManualPageMissingError(file));
    let page = this.publicPages.get(file);
    if (!page) {
      page = this.staticHttp.get(PUBLIC_MANUAL_BASE + file, { responseType: "text" }).pipe(
        map((html) => parseManualPage(file, html)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      this.publicPages.set(file, page);
      // A failed load is not cached, so the next attempt fetches again.
      page.subscribe({ error: () => this.publicPages.delete(file) });
    }
    return page;
  }

  /** The technical manual's contents. Not cached: access can change when a role is granted. */
  technicalContents(): Observable<TechnicalManualContents> {
    return this.http.get<TechnicalManualContents>(this.technicalUrl);
  }

  /** A page of the technical manual. */
  technicalPage(file: string): Observable<ManualPage> {
    if (!isManualPageName(file)) return throwError(() => new ManualPageMissingError(file));
    return this.http.get<TechnicalManualPageDto>(`${this.technicalUrl}/${file}`).pipe(
      map((dto) => parseManualPage(dto.page, dto.html)),
    );
  }

  private get technicalUrl(): string {
    return `${this.env.apiUrl}/api/v1/help/technical`;
  }
}
